import { useEffect, useState } from "react";
import { useBriefing } from "@/context/BriefingContext";

const NAV_LINKS = [
  { href: "#thesis", label: "Thesis" },
  { href: "#pharma", label: "Wedge" },
  { href: "#dark-factory", label: "Dark Factory" },
  { href: "#differentiation", label: "Difference" },
  { href: "#strategic", label: "Sovereignty" },
];

export default function Nav() {
  const { openBriefing } = useBriefing();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[color:var(--g1-bone)]/96 backdrop-blur-md border-b border-[color:var(--g1-rule)]"
          : "bg-[color:var(--g1-bone)]/0"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between">
        {/* Brand */}
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-baseline gap-3 group"
          aria-label="Giga One"
        >
          <span className="font-serif text-xl md:text-2xl tracking-tight text-[color:var(--g1-ink)] leading-none">
            Giga One
          </span>
          <span className="hidden sm:inline-block g1-label text-[color:var(--g1-ink-3)]">
            / INDUSTRIAL AGI
          </span>
        </a>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-10"
          aria-label="Primary"
        >
          {NAV_LINKS.map((l, i) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="g1-label hover:text-[color:var(--g1-ink)] transition-colors"
            >
              <span className="text-[color:var(--g1-ink-3)] mr-2">
                {String(i + 1).padStart(2, "0")}
              </span>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="nav-request-briefing"
            onClick={openBriefing}
            className="hidden md:inline-flex items-center gap-3 px-4 py-2 border border-[color:var(--g1-ink)] text-[color:var(--g1-ink)] font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[color:var(--g1-ink)] hover:text-[color:var(--g1-bone)] transition-colors duration-300"
          >
            Request Briefing
            <span aria-hidden>→</span>
          </button>

          <button
            type="button"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 border border-[color:var(--g1-rule)]"
          >
            <span
              className={`block h-px w-5 bg-[color:var(--g1-ink)] transition-transform ${
                mobileOpen ? "translate-y-[3px] rotate-45" : ""
              }`}
            />
            <span
              className={`block h-px w-5 bg-[color:var(--g1-ink)] transition-transform ${
                mobileOpen ? "-translate-y-[3px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden border-t border-[color:var(--g1-rule)] bg-[color:var(--g1-bone)] transition-[max-height,opacity] duration-500 ${
          mobileOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-5 py-6 flex flex-col divide-y divide-[color:var(--g1-rule)]">
          {NAV_LINKS.map((l, i) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-between py-4 g1-label text-[color:var(--g1-ink)]"
            >
              <span>
                <span className="text-[color:var(--g1-ink-3)] mr-3">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {l.label}
              </span>
              <span aria-hidden className="text-[color:var(--g1-ink-3)]">
                →
              </span>
            </a>
          ))}
          <button
            type="button"
            data-testid="nav-mobile-request-briefing"
            onClick={() => {
              setMobileOpen(false);
              openBriefing();
            }}
            className="mt-4 g1-btn g1-btn-primary w-full justify-between"
          >
            Request Briefing
            <span className="g1-btn-arrow" aria-hidden>
              →
            </span>
          </button>
        </nav>
      </div>
    </header>
  );
}
